/**
 * The Atlas's bodies: the registry rows and the orrery built from them, the
 * shape globes, the ship marker, the belts and the hover halo.
 */

import type * as THREE from 'three';

import type { AtlasLive, AtlasState } from '../atlas';
import { simApi } from '../sim/api.ts';
import { loadBodies } from '../world/bodies.ts';
import { jdTdbFromT } from '../world/clock.ts';
import type { BodyRow } from '../world/types';
import { createBelts, type BeltsHandle } from './belts.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySwitchHandle } from './bodySwitch.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import { DTM_TILE_BASE } from './dtmBase.ts';
import type { FarFieldHandle } from './farField.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { FrameGovernorHandle } from './frameGovernor.ts';
import type { GlobeMaterialHandle } from './globeMaterial.ts';
import type { HexViewHandle } from './hexView.ts';
import { createHoverHalo, type HoverHaloHandle } from './hoverHalo.ts';
import type { InjectsHandle } from './injects.ts';
import type { InspectorHandle } from './inspector.ts';
import type { MountHandle } from './mount.ts';
import type { NavigationHandle } from './navigation.ts';
import {
  createOrrery, type OrreryBody, type OrreryHandle, type OrreryLayer,
} from './orrery.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { LayerPrefsHandle } from './panels/layerPrefs.ts';
import type { SiteChipsHandle } from './panels/siteChips.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { ShaderWarmHandle } from './shaderWarm.ts';
import {
  SHAPE_ASK_MIN_DEG, SHAPE_HOLD_MIN_DEG, createShapeGlobes, drawsAsShape, ellipsoidStandsIn, markShapeMapped,
  type ShapeField,
  type ShapeGlobesHandle,
} from './shapeGlobe.ts';
import { createShipMarker, type ShipMarkerHandle } from './shipMarker.ts';
import type { ViewClockHandle } from './viewClock.ts';
import type { WorldHostState } from './worldHost.ts';
import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';

/** `descent-dojo --arrival --inject late-datum`: an air body takes its datum
 *  only when its shape lands, a few frames into its first focus. */
const lateDatum = devFlag(() => TEST_SWITCHES && urlFlag('latedatum'));

export interface BodySceneDeps {
  readonly frameGovernor: FrameGovernorHandle;
  readonly mount: MountHandle;
  readonly viewClockHandle: ViewClockHandle;
  readonly injects: InjectsHandle;
  readonly worldHostState: WorldHostState;
  readonly live: AtlasLive;
  own(fn: () => void): void;
  readonly renderer: THREE.WebGLRenderer;
  readonly state: AtlasState;
  readonly urlBodyId: { id: string; explicit: boolean };
  readonly bodySwitch: BodySwitchHandle;
  readonly detailGroup: THREE.Group;
  readonly bodyReaders: BodyReadersHandle;
  readonly focusRig: FocusRigHandle;
  readonly inspector: InspectorHandle;
  readonly shaderWarm: ShaderWarmHandle;
  readonly globeMaterial: GlobeMaterialHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly layerPrefs: LayerPrefsHandle;
  readonly siteChips: SiteChipsHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly cameraRig: CameraRigHandle;
  readonly farField: FarFieldHandle;
  readonly navigation: NavigationHandle;
  readonly overlayView: OverlayViewHandle;
}

/** The scene's own state that the mount, the tick and the probe read or write. */
export interface BodySceneState {
  readonly bodyRows: readonly BodyRow[];
  pendingFocus: { bodyId: string; lat: number | null; lon: number | null; altM: number } | null;
  centreAnchorHold: boolean;
  readonly registryFits: number;
  readonly orrery: OrreryHandle | null;
  readonly shipMarker: ShipMarkerHandle | null;
  readonly belts: BeltsHandle | null;
  readonly hoverHalo: HoverHaloHandle | null;
  readonly shapeGlobes: ShapeGlobesHandle | null;
  shapeField: ShapeField | null;
  bodyNode: OrreryBody | null;
}

export interface BodySceneHandle {
  readonly landBodyApplied: boolean;
  /** Builds the ship marker, the belts and the hover halo, and registers their teardown. */
  buildMarkers(): void;
  /** Starts the registry fetch; the orrery and the shape globes are built when it lands. */
  boot(): void;
  showOrrery(on: boolean): void;
  readonly state: BodySceneState;
}

/**
 * Whether a fresh profile opens the Sun on the live photosphere.
 *
 * On, the disc is the white-light Sun of the game date: limb darkening,
 * granulation, and the sunspot record's own groups turning with the clock. Off,
 * it is the committed AIA 304 A composite of one Carrington rotation, which is
 * a measured picture of one month of 2026 and cannot move with the date. The
 * live layer is what the issue asks a player to see, so it is what they get.
 */
export const SOL_LIVE_DEFAULT = true;

export function createBodyScene(deps: BodySceneDeps): BodySceneHandle {
  const { own, renderer, state, urlBodyId } = deps;
  const { atlasRoot, worldFrame, worldRoot } = deps.mount;
  const { viewT, viewRate } = deps.viewClockHandle;
  const { loadStarted, loadFinished } = deps.frameGovernor;

  // ── the orrery ────────────────────────────────────────────────────────────
  //
  // Registry rows, real radii, real distances, positions from the propagator at
  // the clock's instant, every body registered with the camera-relative frame.
  // The fetch is a round trip, so the orrery arrives a frame or two after the
  // mount and everything below it is written to work without one.
  let bodyRows: readonly BodyRow[] = [];
  /**
   * A focus asked for before the rows landed, re-applied when they do.
   *
   * Every radius reads 0 until then, so `tierAt` calls even an ORBIT altitude
   * SYSTEM and the rung the caller asked for cannot be derived yet. The sim's
   * LAND press is that caller: it mounts the Atlas and focuses inside the same
   * press (`src/main.ts`, the land verb).
   */
  let pendingFocus:
    { bodyId: string; lat: number | null; lon: number | null; altM: number } | null = null;
  /**
   * `descent-dojo --underfoot --inject applied-only`: the body rebuild keyed on
   * `appliedBodyId` alone, which is how it read before this fix.
   *
   * Read off the URL for the same reason as the flag above: the press is one
   * click and a switch thrown from outside arms after it.
   */
  const landBodyApplied = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('landbodyapplied') === '1';
  /** `descent-dojo --pivot`'s reference pose: nothing re-derives an anchor while it is set. */
  let centreAnchorHold = false;
  /** Opening heliocentric fits taken when the rows landed, for the regression. */
  let registryFits = 0;
  let orrery: OrreryHandle | null = null;
  /**
   * The you-are-here marker.
   *
   * Built here and not with the orrery: the ship has a position from the first
   * frame and the registry is a fetch away, so the marker does not wait on it.
   */
  let shipMarker: ShipMarkerHandle | null = null;
  /**
   * The main asteroid belt and Kuiper belt.
   *
   * Built here and not with the orrery: both belts are drawn from their own
   * published distributions (beltData.ts) rather than from a registry row, so
   * neither waits on the body fetch.
   */
  let belts: BeltsHandle | null = null;
  /** The ring drawn round whatever the cursor is on, at the orrery rungs. */
  let hoverHalo: HoverHaloHandle | null = null;
  let shapeGlobes: ShapeGlobesHandle | null = null;
  /**
   * The focused body's own L0 height field, kept while its globe is drawn.
   *
   * The hex drape reads it where no pyramid is up: then the displaced globe IS
   * the ground on screen and writes depth where it stands.
   */
  let shapeField: ShapeField | null = null;
  let bodyNode: OrreryBody | null = null;

  function buildMarkers(): void {
    shipMarker = createShipMarker({
      frame: worldFrame,
      at: () => simApi.ship.at(),
      onNeedsRender: () => { deps.live.needsRender = true; },
    });
    own(() => { shipMarker?.dispose(); shipMarker = null; });

    belts = createBelts({
      frame: worldFrame,
      onNeedsRender: () => { deps.live.needsRender = true; },
    });
    own(() => { belts?.dispose(); belts = null; });

    hoverHalo = createHoverHalo({ frame: worldFrame });
    own(() => { hoverHalo?.dispose(); hoverHalo = null; });
  }

  /**
   * The registry, and the orrery built from it.
   *
   * Async on purpose: the Atlas draws the star field and its own chrome on the
   * first frame and the bodies arrive when the fetch lands. Nothing between here
   * and the tick loop may assume `orrery` is non-null.
   */
  function boot(): void {
    void loadBodies()
      .then((rows) => {
        if (deps.live.disposed) return;
        bodyRows = rows;
        if (urlBodyId.explicit && !rows.some((r) => r.id === urlBodyId.id)) {
          deps.bodySwitch.refuseBody(urlBodyId.id, rows);
          return;
        }
        deps.inspector.syncHeroCta();
        const shaped = new Set(rows.filter(drawsAsShape).map((r) => r.id));
        const standing = new Set(rows.filter(ellipsoidStandsIn).map((r) => r.id));
        const heldLast = new Set<string>();
        orrery = createOrrery({
          frame: worldFrame,
          scene: atlasRoot,
          rows,
          jdTdb: () => deps.worldHostState.worldEpochJd ?? jdTdbFromT(viewT()),
          clockRate: () => (deps.worldHostState.worldEpochJd === null ? viewRate() : 0),
          onLoadStart: loadStarted,
          onLoadEnd: loadFinished,
          onNeedsRender: () => { deps.live.needsRender = true; },
          uploadTexture: (t) => renderer.initTexture(t),
          // Not compiled here: this runs on a fetch's own callback, outside
          // `stage.begin()`, and the renderer's shadow-map state out there is
          // the sim's - which keys a DIFFERENT program from the one the Atlas
          // then draws with, so every one of these was linked twice. The warm
          // walks the whole subtree from inside the bracket instead.
          // Only while the mount is still settling. A pass costs a walk of the
          // whole subtree and makes the next draw rebuild every material's
          // uniform list, which is most of a second once the pyramid has
          // streamed - so once the descent can be ridden with nothing left to
          // link, a body's own texture arriving is not a reason to link again.
          linkPrograms: () => {
            if (deps.shaderWarm.warmSettled()) return;
            deps.shaderWarm.warmDue = true;
            deps.live.needsRender = true;
          },
          shapeHold: (id, deg) => {
            const inj = deps.injects.state;
            if (deg < SHAPE_ASK_MIN_DEG || !shaped.has(id) || !shapeGlobes || inj.shapeSphereDebug) return false;
            const loading = shapeGlobes.holding(id, performance.now());
            // A body with an ellipsoid standing in is drawn meanwhile, never held.
            const held = loading && deg >= SHAPE_HOLD_MIN_DEG && (!standing.has(id) || inj.shapeRoundStandIn) && !inj.shapeLateSwap;
            // Drawn on while held and on the frame the hold lapses, which no
            // load callback announces.
            if (held || heldLast.has(id)) deps.live.needsRender = true;
            if (held) heldLast.add(id);
            else heldLast.delete(id);
            return held;
          },
        });
        // The globe as the body's own shape. Per body focused rather than for the
        // whole registry: the six L0 height tiles are 780 KB and the mesh 5.5 MB
        // of buffers, and an unfocused row is at the angular floor.
        shapeGlobes = createShapeGlobes({
          baseUrl: DTM_TILE_BASE,
          unitRadiusM: (id) => deps.bodyReaders.rowById(id)?.radius_m ?? null,
          onReady: (id, geometry, field) => {
            if (deps.injects.state.shapeSphereDebug) return;
            if (id === deps.bodySwitch.state.appliedBodyId) shapeField = field;
            const b = orrery?.byId(id) ?? null;
            if (b) {
              // The equirect map has no seam to cut a cubesphere along, so this
              // body's map is sampled by direction from here on - on the sphere
              // too, where the result is the same and putting the uv path back
              // would cost a link. Before the geometry, because the swap pays
              // the program link on the frame it lands.
              const mat = b.mesh.material as THREE.MeshStandardMaterial;
              markShapeMapped(mat);
              deps.globeMaterial.syncGlobeShader(mat, b.mesh === bodyNode?.mesh, b.coverage);
            }
            orrery?.setShapeGeometry(id, geometry, field.datumRadiusM);
            deps.live.needsRender = true;
          },
        });
        own(() => {
          orrery?.dispose();
          orrery = null;
          bodyNode = null;
          shapeGlobes?.dispose();
          shapeGlobes = null;
        });
        // The air's march ends on the drawn radius, and Earth's datum is 7.1 km
        // over its registry radius: taken at its shape, the first frames drew 12% bright.
        for (const row of rows) {
          if (lateDatum() || row.atmosphere.kind !== 'data'
            || row.availability.coverage.height !== 'baked') continue;
          void shapeGlobes.datum(row.id).then((datumM) => {
            if (datumM !== null) orrery?.setDatumRadius(row.id, datumM);
          }, () => {});
        }
        deps.bodySwitch.syncBody();
        const ladderRow = deps.bodyReaders.rowById(deps.bodyReaders.ladderBody());
        // A control armed before the globes existed still holds: the orrery
        // builds one fill per body from its own row and knows nothing of it.
        if (deps.pyramidView.unmeasuredFill !== null) orrery.setUnmeasuredFill(deps.pyramidView.unmeasuredFill);
        const inj = deps.injects.state;
        if (inj.shapeSphereDebug || inj.shapeLateSwap || inj.shapeRoundStandIn) orrery.setRoundGlobes(true);
        // The panel's own switches, which were readable from storage long before
        // there was an orrery to apply them to.
        for (const row of deps.layerPrefs.rows()) {
          if (row.id !== 'hexes') orrery.setLayerVisible(row.id as OrreryLayer, row.get());
        }
        // `hexes` is the one row above that reaches the overlay rather than the
        // orrery, and its gate reads the rows this callback just landed.
        deps.hexViewSeam.syncHexVisibility();
        deps.pyramidView.setArtificialLayer(deps.pyramidView.artificialLayerOn());
        orrery.setSolLive(deps.layerPrefs.get('solLive') ?? SOL_LIVE_DEFAULT);
        orrery.setRingsVisible(state.tier <= 2);
        // The heliocentric framing is a function of where the bodies actually
        // are, so it cannot be a constant: it holds the rows inside
        // `OPENING_FIT_MAX_A_AU` with a margin, and the wheel and the manifest
        // panel reach the rest. Every one of those positions is the origin until
        // the propagator has run once - without this the fit falls back to its
        // own constant and the opening view is 127 AU whatever the registry says.
        orrery.propagate();
        // A focus asked for before the rows landed is honoured here, where the
        // body finally has a radius to read its rung against: the opening fit
        // frames sol, so taking it on a SYSTEM reading would answer a press that
        // named Mars with the Sun.
        const asked = pendingFocus;
        const askedBefore = pendingFocus !== null;
        pendingFocus = null;
        if (asked) {
          deps.focusRig.focusCam.goTo(asked.bodyId, asked.lat, asked.lon, asked.altM);
          deps.navigation.syncTierToAltitude();
          if (state.tier > 0) deps.cameraRig.applyTierFraming(state.tier, asked.altM);
        } else if (state.tier === 0) {
          // Counted only where a focus WAS asked for and this fit took it: the
          // Atlas mounts at boot with nothing asked for, and the heliocentric fit
          // it opens on there is the opening view rather than an answer to a
          // press that named a body.
          if (askedBefore) registryFits++;
          // Not over a view the player has already moved: the rows land seconds
          // into the session on a cold cache, and a wheel spent on Sol before
          // then was being thrown back out to the whole system.
          if (deps.focusRig.openingFitHeld()) deps.focusRig.fitOpening(deps.cameraRig.aspect());
        } else deps.cameraRig.applyTierFraming(state.tier);
        // Fetched here rather than at mount or on first descent: which
        // quadrangle is open and where the hex ring sits are answers only the
        // site registry has, and a refused body must not reach for it at all.
        // Chained after the registry's own `applyState`, which clears the card:
        // a report the next frame wipes is no report at all.
        void deps.siteChips.ensureSites().then(() => {
          if (!deps.live.disposed && urlBodyId.explicit && ladderRow && !ladderRow.availability.descend) {
            deps.bodySwitch.reportNoPyramid(ladderRow);
          }
        });
        deps.bodySwitch.rebuildDescentTarget();
        deps.live.cameraDirty = true;
        deps.live.needsRender = true;
        deps.overlayView.renderOverlay();
      })
      .catch((err: unknown) => {
        console.warn('[atlas] the body registry did not load; the orrery is empty:', err);
      });
  }

  function showOrrery(on: boolean): void {
    worldRoot.visible = on;
    deps.farField.syncGroundSky();
    if (orrery) {
      orrery.sol.mesh.visible = on;
      for (const l of orrery.lights) l.visible = on;
    }
    deps.detailGroup.visible = on && deps.bodyReaders.descendable() && state.tier >= 1;
    deps.live.needsRender = true;
  }

  return {
    landBodyApplied,
    buildMarkers,
    boot,
    showOrrery,
    state: {
      get bodyRows() { return bodyRows; },
      get pendingFocus() { return pendingFocus; },
      set pendingFocus(v) { pendingFocus = v; },
      get centreAnchorHold() { return centreAnchorHold; },
      set centreAnchorHold(v) { centreAnchorHold = v; },
      get registryFits() { return registryFits; },
      get orrery() { return orrery; },
      get shipMarker() { return shipMarker; },
      get belts() { return belts; },
      get hoverHalo() { return hoverHalo; },
      get shapeGlobes() { return shapeGlobes; },
      get shapeField() { return shapeField; },
      set shapeField(v) { shapeField = v; },
      get bodyNode() { return bodyNode; },
      set bodyNode(v) { bodyNode = v; },
    },
  };
}
